// Light, dark or match the device, kept in this browser. The resolved
// choice goes on <html data-theme>, which styles/tokens.css reads.
import { useEffect, useState } from 'react';
import { track } from '@/data/analytics';
import { readPreference, writePreference } from '@/data/preferences';

export type ThemeChoice = 'system' | 'light' | 'dark';

const listeners = new Set<(choice: ThemeChoice) => void>();

export function readTheme(): ThemeChoice {
  const saved = readPreference('theme');
  return saved === 'light' || saved === 'dark' ? saved : 'system';
}

/** Applies the saved choice; on "match device", follows the device as it changes. */
export function applyTheme() {
  const choice = readTheme();
  const dark = choice === 'dark' || (choice === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#0A0C0B' : '#F6F3EC');
}

export function startTheme() {
  applyTheme();
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', applyTheme);
}

/** Saves the choice, applies it, and tells every open control. */
export function setTheme(next: ThemeChoice) {
  track('setting_changed', { setting: 'theme' });
  writePreference('theme', next === 'system' ? null : next);
  applyTheme();
  for (const listener of listeners) listener(next);
}

/** The theme choice, and a setter that saves and applies it. */
export function useTheme(): [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);
  useEffect(() => {
    listeners.add(setChoice);
    return () => {
      listeners.delete(setChoice);
    };
  }, []);
  return [choice, setTheme];
}

/** Whether the device itself is in dark mode, including as it changes. */
export function useSystemDark(): boolean {
  const [dark, setDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setDark(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);
  return dark;
}
